import type { MosaicLayout, StreamConfig, ThumbRect } from './types'

export interface GridSpec {
  cols: number
  rows: number
  tileW: number
  tileH: number
  /** Size of the stacked grid, which can be a hair smaller than the requested output. */
  canvasW: number
  canvasH: number
}

const FIXED: Record<Exclude<MosaicLayout, 'auto'>, { cols: number; rows: number }> = {
  '1x1': { cols: 1, rows: 1 },
  '2x1': { cols: 2, rows: 1 },
  '2x2': { cols: 2, rows: 2 },
  '3x2': { cols: 3, rows: 2 },
  '3x3': { cols: 3, rows: 3 },
  '4x2': { cols: 4, rows: 2 },
  '4x3': { cols: 4, rows: 3 }
}

/** Smallest sensible grid for a camera count. */
export function autoGrid(count: number): { cols: number; rows: number } {
  if (count <= 1) return { cols: 1, rows: 1 }
  if (count === 2) return { cols: 2, rows: 1 }
  if (count <= 4) return { cols: 2, rows: 2 }
  if (count <= 6) return { cols: 3, rows: 2 }
  if (count <= 9) return { cols: 3, rows: 3 }
  const cols = Math.ceil(Math.sqrt(count))
  return { cols, rows: Math.ceil(count / cols) }
}

export function computeGrid(
  count: number,
  layout: MosaicLayout,
  outWidth: number,
  outHeight: number
): GridSpec {
  const base = layout === 'auto' ? autoGrid(count) : FIXED[layout]
  // A fixed layout that cannot hold every camera would silently drop tiles.
  const cols = Math.max(base.cols, Math.ceil(count / base.rows) > base.cols ? Math.ceil(count / base.rows) : base.cols)
  const rows = Math.max(base.rows, Math.ceil(count / cols))

  // H.264 needs even dimensions on both the tile and the canvas.
  const tileW = Math.max(2, Math.floor(outWidth / cols / 2) * 2)
  const tileH = Math.max(2, Math.floor(outHeight / rows / 2) * 2)

  return { cols, rows, tileW, tileH, canvasW: tileW * cols, canvasH: tileH * rows }
}

export interface SpotlightSpec {
  thumbW: number
  thumbH: number
  /** Top-left corner of each thumbnail that fits on the canvas. */
  positions: { x: number; y: number }[]
  /** How many thumbnails the strip can hold at this size. */
  capacity: number
  /** Thumbnails that did not fit and are therefore not drawn. */
  dropped: number
  horizontal: boolean
}

const even = (n: number): number => Math.max(2, Math.round(n / 2) * 2)

/**
 * Lays out a strip of thumbnails along one edge of the canvas. Thumbnails keep the
 * canvas aspect ratio, so a 16:9 wall gets 16:9 minis.
 */
export function computeSpotlight(thumbCount: number, config: StreamConfig): SpotlightSpec {
  const { outputWidth: W, outputHeight: H, stripPosition, thumbGap, thumbMargin } = config
  const horizontal = stripPosition === 'top' || stripPosition === 'bottom'
  const scale = Math.min(Math.max(config.thumbScale, 0.05), 0.5)

  const thumbW = horizontal ? even(W * scale) : even(H * scale * (W / H))
  const thumbH = horizontal ? even(thumbW * (H / W)) : even(H * scale)

  // How many fit end to end along the strip, given the gaps and edge inset.
  const span = horizontal ? W - thumbMargin * 2 : H - thumbMargin * 2
  const step = (horizontal ? thumbW : thumbH) + thumbGap
  const capacity = Math.max(0, Math.floor((span + thumbGap) / step))
  const shown = Math.min(thumbCount, capacity)

  // Where the run of thumbnails sits along its long axis. The margin is honoured
  // at both ends, so 'start'/'end' sit inside the inset rather than on the edge.
  const used = shown * step - thumbGap
  const axis = horizontal ? W : H
  const free = Math.max(0, axis - thumbMargin * 2 - used)
  const start =
    config.stripAlign === 'start'
      ? thumbMargin
      : config.stripAlign === 'end'
        ? thumbMargin + free
        : Math.round((axis - used) / 2)

  const positions: { x: number; y: number }[] = []
  for (let i = 0; i < shown; i++) {
    if (horizontal) {
      const y = stripPosition === 'top' ? thumbMargin : H - thumbH - thumbMargin
      positions.push({ x: start + i * step, y })
    } else {
      const x = stripPosition === 'left' ? thumbMargin : W - thumbW - thumbMargin
      positions.push({ x, y: start + i * step })
    }
  }

  return { thumbW, thumbH, positions, capacity, dropped: thumbCount - shown, horizontal }
}

/** Smallest mini that still reads as a picture rather than a speck. */
export const MIN_THUMB_W = 48
export const MIN_THUMB_H = 32

/**
 * A hand-placed rect coerced onto the canvas: even integers (chroma subsampling
 * dislikes odd offsets), floored at the minimum size, and pulled fully inside
 * the output so ffmpeg never overlays off-canvas.
 */
export function clampThumbRect(rect: ThumbRect, config: StreamConfig): ThumbRect {
  const even = (n: number): number => Math.round(n / 2) * 2
  const W = config.outputWidth
  const H = config.outputHeight
  const w = Math.min(Math.max(even(rect.w), MIN_THUMB_W), even(W))
  const h = Math.min(Math.max(even(rect.h), MIN_THUMB_H), even(H))
  const x = Math.min(Math.max(even(rect.x), 0), W - w)
  const y = Math.min(Math.max(even(rect.y), 0), H - h)
  return { x, y, w, h }
}

export interface SpotThumb {
  /** Index into the enabled-camera list. */
  cam: number
  x: number
  y: number
  w: number
  h: number
  /** True when the operator placed this mini by hand. */
  custom: boolean
}

export interface SpotlightPlan {
  /** Enabled-camera index filling the background. */
  main: number
  /** Every mini that will be drawn, hand-placed ones last (on top). */
  thumbs: SpotThumb[]
  /** Auto-strip geometry, for the UI's hints. */
  stripW: number
  stripH: number
  capacity: number
  /** Auto-strip minis that did not fit and are not drawn. */
  dropped: number
}

/**
 * The one description of a spotlight composition, shared by the composition
 * board and the filter graph so the arrangement dragged out in the UI is
 * exactly the arrangement that goes on air. Cameras with a hand-placed rect
 * sit wherever they were dropped; the rest flow through the automatic strip.
 */
export function planSpotlight(config: StreamConfig, cameraIds: string[]): SpotlightPlan {
  const count = cameraIds.length
  const main = Math.min(Math.max(config.mainCameraIndex, 0), Math.max(count - 1, 0))

  // Layering: listed ids in their configured sequence, unlisted ones below
  // them in camera-list order. This one sequence decides both the strip
  // seating and, because overlays chain in thumb order, who draws over whom.
  const rank = new Map((config.spotlightOrder ?? []).map((id, i) => [id, i]))
  const others = Array.from({ length: count }, (_, i) => i)
    .filter((i) => i !== main)
    .sort((a, b) => (rank.get(cameraIds[a]) ?? a - count) - (rank.get(cameraIds[b]) ?? b - count))

  const auto = others.filter((i) => !config.spotlightRects?.[cameraIds[i]])
  const spec = computeSpotlight(auto.length, config)
  const slot = new Map(spec.positions.map((p, n) => [auto[n], p]))

  const thumbs: SpotThumb[] = []
  for (const i of others) {
    const rect = config.spotlightRects?.[cameraIds[i]]
    if (rect) {
      thumbs.push({ cam: i, ...clampThumbRect(rect, config), custom: true })
    } else {
      const p = slot.get(i)
      // Strip minis past capacity are not drawn; the plan reports the count.
      if (p) thumbs.push({ cam: i, x: p.x, y: p.y, w: spec.thumbW, h: spec.thumbH, custom: false })
    }
  }
  return {
    main,
    thumbs,
    stripW: spec.thumbW,
    stripH: spec.thumbH,
    capacity: spec.capacity,
    dropped: auto.length - spec.positions.length
  }
}

export interface GridRects {
  cells: { x: number; y: number }[]
  /** Tight bounding box of the stacked tiles — what xstack will output. */
  boxW: number
  boxH: number
}

/**
 * Tile positions inside the stacked block. A partial last row follows the
 * horizontal alignment; the block itself is placed on the canvas afterwards.
 */
export function gridRects(count: number, grid: GridSpec, config: StreamConfig): GridRects {
  const usedRows = Math.max(1, Math.ceil(count / grid.cols))
  const widestRow = Math.min(count, grid.cols) * grid.tileW
  const cells: { x: number; y: number }[] = []

  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / grid.cols)
    const inRow = row === usedRows - 1 ? count - row * grid.cols : grid.cols
    const rowW = inRow * grid.tileW
    const offX =
      config.gridAlignH === 'left'
        ? 0
        : config.gridAlignH === 'right'
          ? widestRow - rowW
          : Math.round((widestRow - rowW) / 2)
    cells.push({ x: offX + (i - row * grid.cols) * grid.tileW, y: row * grid.tileH })
  }

  return { cells, boxW: widestRow, boxH: usedRows * grid.tileH }
}

/** Where the stacked block lands on the output canvas. */
export function gridOffset(
  boxW: number,
  boxH: number,
  config: StreamConfig
): { x: number; y: number } {
  const x =
    config.gridAlignH === 'left'
      ? 0
      : config.gridAlignH === 'right'
        ? config.outputWidth - boxW
        : Math.round((config.outputWidth - boxW) / 2)
  const y =
    config.gridAlignV === 'top'
      ? 0
      : config.gridAlignV === 'bottom'
        ? config.outputHeight - boxH
        : Math.round((config.outputHeight - boxH) / 2)
  return { x: Math.max(0, x), y: Math.max(0, y) }
}
